import React, { useState, useMemo } from 'react';
import { Calculator, Users, Plus, Eye, Edit, Star, Ban, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import type { TaxRate, TaxRateMaintenanceState, TaxRatePaginationInfo } from '../../../../types/taxRateMaintenance';
import { mockTaxRates } from '../../../../data/mockTaxRates';
import TaxRateDetailsModal from './components/TaxRateDetailsModal';
import '../../../../styles/taxRateMaintenance.css';

interface TaxRateMaintenanceProps {
  className?: string;
  onEditTaxRate?: (taxRate: TaxRate) => void;
}

const TaxRateMaintenance: React.FC<TaxRateMaintenanceProps> = ({ className = '', onEditTaxRate }) => {
  const [state, setState] = useState<TaxRateMaintenanceState>({
    taxRates: mockTaxRates,
    searchTerm: '',
    currentPage: 1,
    itemsPerPage: 5,
    totalTaxRates: mockTaxRates.length,
    selectedTaxRates: [],
    isLoading: false
  });

  const [viewDetailsModal, setViewDetailsModal] = useState<{
    isOpen: boolean;
    taxRate: TaxRate | null;
  }>({ isOpen: false, taxRate: null });

  // Filter tax rates based on search term
  const filteredTaxRates = useMemo(() => {
    if (!state.searchTerm) {
      return state.taxRates;
    }

    const searchLower = state.searchTerm.toLowerCase();
    return state.taxRates.filter(tr =>
      tr.taxRateValue.toLowerCase().includes(searchLower) ||
      tr.description.toLowerCase().includes(searchLower)
    );
  }, [state.taxRates, state.searchTerm]);

  // Calculate pagination
  const paginationInfo = useMemo((): TaxRatePaginationInfo => {
    const totalTaxRates = filteredTaxRates.length;
    const totalPages = Math.ceil(totalTaxRates / state.itemsPerPage);
    const startTaxRate = (state.currentPage - 1) * state.itemsPerPage + 1;
    const endTaxRate = Math.min(state.currentPage * state.itemsPerPage, totalTaxRates);

    return {
      currentPage: state.currentPage,
      totalPages,
      totalTaxRates,
      itemsPerPage: state.itemsPerPage,
      startTaxRate: totalTaxRates > 0 ? startTaxRate : 0,
      endTaxRate: totalTaxRates > 0 ? endTaxRate : 0
    };
  }, [filteredTaxRates.length, state.currentPage, state.itemsPerPage]);

  // Get current page tax rates
  const currentPageTaxRates = useMemo(() => {
    const startIndex = (state.currentPage - 1) * state.itemsPerPage;
    const endIndex = startIndex + state.itemsPerPage;
    return filteredTaxRates.slice(startIndex, endIndex);
  }, [filteredTaxRates, state.currentPage, state.itemsPerPage]);

  const handleSearchChange = (searchTerm: string) => {
    setState(prev => ({
      ...prev,
      searchTerm,
      currentPage: 1 // Reset to first page when searching
    }));
  };

  const handlePageChange = (page: number) => {
    setState(prev => ({
      ...prev,
      currentPage: page
    }));
  };

  const handleCreateNew = () => {
    alert('Create New Tax Rate Modal\n\nThis would open a form with:\n- Tax Rate Value input\n- Description input\n- Default status configuration\n- Status configuration');
  };

  const handleViewTaxRate = (taxRate: TaxRate) => {
    setViewDetailsModal({ isOpen: true, taxRate });
  };

  const handleEditTaxRate = (taxRate: TaxRate) => {
    if (onEditTaxRate) {
      onEditTaxRate(taxRate);
    } else {
      alert(`Edit Tax Rate Modal\n\nThis would open an edit form for:\n- Tax Rate Value: ${taxRate.taxRateValue}\n- Description: ${taxRate.description}\n- Default and status modifications`);
    }
  };

  const handleSetDefaultTaxRate = (taxRate: TaxRate) => {
    const confirmSetDefault = confirm(`Are you sure you want to set "${taxRate.taxRateValue}" as the default tax rate?\n\nThis will remove the default status from any other tax rate.`);
    if (confirmSetDefault) {
      setState(prev => ({
        ...prev,
        taxRates: prev.taxRates.map(tr => ({
          ...tr,
          isDefault: tr.id === taxRate.id,
          lastModified: tr.id === taxRate.id ? new Date() : tr.lastModified
        }))
      }));
      alert(`Tax Rate "${taxRate.taxRateValue}" has been set as the default tax rate.`);
    }
  };

  const handleDeactivateTaxRate = (taxRate: TaxRate) => {
    const action = taxRate.isActive ? 'deactivate' : 'activate';
    const confirmAction = confirm(`Are you sure you want to ${action} tax rate "${taxRate.taxRateValue}"?\n\n${taxRate.isActive ? 'This will make it unavailable for new transactions.' : 'This will make it available for new transactions.'}`);
    if (confirmAction) {
      setState(prev => ({
        ...prev,
        taxRates: prev.taxRates.map(tr => 
          tr.id === taxRate.id 
            ? { ...tr, isActive: !tr.isActive, lastModified: new Date() }
            : tr
        )
      }));
      alert(`Tax Rate "${taxRate.taxRateValue}" has been ${taxRate.isActive ? 'deactivated' : 'activated'} successfully.`);
    }
  };

  const renderPagination = () => {
    return (
      <div className="tax-rate-pagination">
        <div className="pagination-info">
          Showing {paginationInfo.startTaxRate} to {paginationInfo.endTaxRate} of {paginationInfo.totalTaxRates} tax rates
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(paginationInfo.currentPage - 1)}
            disabled={paginationInfo.currentPage === 1}
          >
            <ChevronLeft size={16} />
          </button>
          
          {Array.from({ length: paginationInfo.totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              className={`pagination-number ${page === paginationInfo.currentPage ? 'active' : ''}`}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          ))}
          
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(paginationInfo.currentPage + 1)}
            disabled={paginationInfo.currentPage === paginationInfo.totalPages}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={`tax-rate-maintenance ${className}`}>
      {/* Header */}
      <div className="tax-rate-maintenance-header">
        <div className="tax-rate-maintenance-header-left">
          <div className="tax-rate-maintenance-icon">
            <Calculator size={24} />
          </div>
          <div className="tax-rate-maintenance-title-section">
            <h1 className="tax-rate-maintenance-title">Manage Tax Rate</h1>
            <div className="tax-rate-maintenance-context">
              Manage and oversee tax rate records and activities
            </div>
          </div>
        </div>
        
        <div className="tax-rate-maintenance-header-actions">
          <button
            className="btn-create-new-tax-rate"
            onClick={handleCreateNew}
          >
            <Plus size={16} />
            Create New
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="tax-rate-maintenance-search-section">
        <div className="tax-rate-search-controls">
          <div className="tax-rate-search-input-container">
            <Search size={16} className="tax-rate-search-icon" />
            <input
              type="text"
              placeholder="Search by Tax Rate value or tax rate description..."
              value={state.searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="tax-rate-search-input"
            />
            <div className="tax-rate-search-tooltip">
              Search by Tax Rate value or tax rate description. Type to filter results instantly.
            </div>
          </div>
        </div>
        
        <div className="tax-rate-count-info">
          <Users size={16} className="tax-rate-count-icon" />
          <span className="tax-rate-count-text">{filteredTaxRates.length} tax rates found</span>
        </div>
      </div>

      {/* Table */}
      <div className="tax-rate-table-container">
        <table className="tax-rate-table">
          <thead className="tax-rate-table-header">
            <tr>
              <th className="tax-rate-value-column">TAX RATE VALUE</th>
              <th className="description-column">DESCRIPTION</th>
              <th className="default-column">DEFAULT</th>
              <th className="status-column">STATUS</th>
              <th className="actions-column">ACTIONS</th>
            </tr>
          </thead>
          <tbody className="tax-rate-table-body">
            {currentPageTaxRates.map((taxRate) => (
              <tr key={taxRate.id} className="tax-rate-row">
                <td className="tax-rate-value-cell">{taxRate.taxRateValue}</td>
                <td className="description-cell">
                  {taxRate.description}
                  {taxRate.isDefault && <span className="default-badge">DEFAULT</span>}
                </td>
                <td className="default-cell">
                  <div className={`default-indicator ${taxRate.isDefault ? 'yes' : 'no'}`}>
                    {taxRate.isDefault ? 'Yes' : 'No'}
                  </div>
                </td>
                <td className="status-cell">
                  <div className={`status-indicator ${taxRate.isActive ? 'active' : 'inactive'}`}>
                    <div className={`status-dot ${taxRate.isActive ? 'active' : 'inactive'}`}></div>
                    {taxRate.isActive ? 'Active' : 'Inactive'}
                  </div>
                </td>
                <td>
                  <div className="tax-rate-action-buttons">
                    <button
                      className="tax-rate-action-btn view"
                      onClick={() => handleViewTaxRate(taxRate)}
                      title="View Tax Rate"
                    >
                      <Eye size={14} />
                    </button>
                    <button
                      className="tax-rate-action-btn edit"
                      onClick={() => handleEditTaxRate(taxRate)}
                      title="Edit Tax Rate"
                    >
                      <Edit size={14} />
                    </button>
                    <button
                      className="tax-rate-action-btn set-default"
                      onClick={() => handleSetDefaultTaxRate(taxRate)}
                      title="Set as Default"
                    >
                      <Star size={14} />
                    </button>
                    <button
                      className="tax-rate-action-btn deactivate"
                      onClick={() => handleDeactivateTaxRate(taxRate)}
                      title={taxRate.isActive ? "Deactivate Tax Rate" : "Activate Tax Rate"}
                    >
                      <Ban size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {renderPagination()}

      {/* Tax Rate Details Modal */}
      <TaxRateDetailsModal
        isOpen={viewDetailsModal.isOpen}
        onClose={() => setViewDetailsModal({ isOpen: false, taxRate: null })}
        taxRate={viewDetailsModal.taxRate}
        onEdit={(taxRate: TaxRate) => {
          setViewDetailsModal({ isOpen: false, taxRate: null });
          handleEditTaxRate(taxRate);
        }}
      />
    </div>
  );
};

export default TaxRateMaintenance;